import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  motion,
  useScroll,
  useTransform,
  useInView,
} from 'framer-motion';
import {
  ArrowRight,
  ArrowUpRight,
  Star,
  Zap,
  Shield,
  Wind,
  Footprints,
  Award,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

/* ----------------------------------------------------------------------------
   GOFIT — Homepage (light + bilingual FR/AR)
   "Go Faster. Go Further. GoFit."
---------------------------------------------------------------------------- */

const featuredProducts = [
  { id: 'h1-bleu', key: 'h1Bleu', price: 2800, image: '/products/h1-bleu.jpg', href: '/homme' },
  { id: 'f1-bleu', key: 'f1Bleu', price: 2800, image: '/products/f1-bleu.jpg', href: '/femme' },
  { id: 'h1-noir', key: 'h1Noir', price: 2800, image: '/products/h1-noir.jpg', href: '/homme' },
  { id: 'f1-gris', key: 'f1Gris', price: 2800, image: '/products/f1-gris.jpg', href: '/femme' },
];

const featureIcons = [Footprints, Wind, Shield, Zap];

/* -------------------------------- HERO -------------------------------- */

const Hero = () => {
  const { t, dir, lang } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);
  const opacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  const tagline = ['Go Faster.', 'Go Further.', 'GoFit.'];
  const isAr = lang === 'ar';

  return (
    <section ref={ref} className="relative min-h-screen w-full overflow-hidden bg-white">
      {/* Background image */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src="/brand/hero-runner.png"
          alt=""
          className="absolute right-0 top-0 h-full w-full md:w-[60%] object-cover object-[center_30%]"
          draggable={false}
        />
        {/* Soft white fade from left, diagonal red glow */}
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 md:via-white/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-white" />
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-gofit-red/10 blur-3xl" />
      </motion.div>

      {/* Diagonal speed lines (subtle on light) */}
      <div className="absolute inset-0 z-[1] pointer-events-none overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute h-px bg-gradient-to-r from-transparent via-gofit-red/30 to-transparent"
            style={{
              top: `${15 + i * 12}%`,
              width: '40%',
              left: `${10 + i * 8}%`,
              transform: 'skewY(-8deg)',
            }}
            initial={{ x: '-100%', opacity: 0 }}
            animate={{ x: '300%', opacity: [0, 1, 0] }}
            transition={{
              duration: 3.5 + i * 0.4,
              repeat: Infinity,
              delay: i * 0.6,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>

      <motion.div style={{ opacity }} className="relative z-10 container mx-auto px-6 min-h-screen flex flex-col justify-end pb-24 md:pb-32">
        {/* Badge made in DZ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="inline-flex items-center gap-2 px-3 py-1.5 mb-8 w-fit rounded-full border border-gofit-ink/10 bg-white/80 backdrop-blur-sm text-xs uppercase tracking-[0.2em] font-medium text-gofit-ink"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-gofit-red animate-pulse" />
          {t('hero.badge')}
        </motion.div>

        {/* Tagline reveal — kept in English (brand identity) */}
        <h1 className="font-display font-bold leading-[0.9] tracking-tight text-gofit-ink">
          {tagline.map((line, i) => (
            <motion.span
              key={line}
              initial={{ opacity: 0, y: 80, rotateX: -45 }}
              animate={{ opacity: 1, y: 0, rotateX: 0 }}
              transition={{ delay: 0.4 + i * 0.18, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className={`block text-[13vw] sm:text-[11vw] md:text-[10vw] lg:text-[9rem] xl:text-[10rem] ${
                i === 2 ? 'text-gofit-red' : 'text-gofit-ink'
              }`}
              style={{ transformOrigin: 'bottom', direction: 'ltr' }}
            >
              {line}
            </motion.span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <Link
            to="/collections"
            className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-gofit-red text-white font-semibold tracking-wide uppercase text-sm rounded-full hover:bg-gofit-red-600 transition-all duration-300 hover:scale-[1.02] active:scale-95 shadow-[0_10px_30px_rgba(225,15,30,0.25)]"
          >
            {t('hero.ctaCollection')}
            <ArrowRight className={`w-4 h-4 transition-transform group-hover:translate-x-1 ${isAr ? 'rotate-180' : ''}`} />
          </Link>
          <Link
            to="/homme"
            className="group inline-flex items-center justify-center gap-3 px-8 py-4 border border-gofit-ink/15 text-gofit-ink font-semibold tracking-wide uppercase text-sm rounded-full hover:bg-gofit-ink hover:text-white transition-all duration-300"
          >
            {t('hero.ctaMen')}
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-gofit-ink/50 text-xs uppercase tracking-[0.3em] flex flex-col items-center gap-2"
      >
        <span>{t('hero.scroll')}</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          className="w-px h-8 bg-gradient-to-b from-gofit-ink/50 to-transparent"
        />
      </motion.div>
    </section>
  );
};

/* ----------------------------- TICKER ----------------------------- */

const Ticker = () => {
  const { t } = useLanguage();
  const items = t('ticker');
  const list = Array.isArray(items) ? items : [];
  const repeated = [...list, ...list, ...list];
  return (
    <section className="bg-gofit-red text-white py-5 overflow-hidden border-y border-gofit-red-600">
      <div className="flex whitespace-nowrap">
        <motion.div
          className="flex gap-12 pr-12"
          animate={{ x: ['0%', '-50%'] }}
          transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
          style={{ direction: 'ltr' }}
        >
          {repeated.map((item, i) => (
            <span key={i} className="font-display font-bold text-2xl md:text-3xl uppercase tracking-tight flex items-center gap-12">
              {item}
              <span className="w-2 h-2 rounded-full bg-white/80 inline-block" />
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

/* --------------------------- MADE IN DZ --------------------------- */

const MadeInDz = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-15% 0px' });
  return (
    <section ref={ref} className="relative bg-white text-gofit-ink py-28 md:py-40 overflow-hidden">
      <div className="container mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7"
        >
          <div className="text-gofit-red text-xs uppercase tracking-[0.4em] mb-6 font-medium">
            {t('madeInDz.kicker')}
          </div>
          <h2 className="font-display font-bold text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight mb-8">
            {t('madeInDz.title1')} <br />
            <span className="text-gofit-red">{t('madeInDz.title2')}</span>
          </h2>
          <p className="text-gofit-ink/65 text-lg md:text-xl leading-relaxed max-w-xl mb-8">
            {t('madeInDz.body')} <span className="text-gofit-ink font-medium">{t('madeInDz.bodyStrong')}</span>
          </p>
          <div className="flex flex-wrap gap-6 text-sm">
            <div className="flex items-center gap-2 text-gofit-ink/60">
              <Award className="w-4 h-4 text-gofit-red" />
              {t('madeInDz.stat1')}
            </div>
            <div className="flex items-center gap-2 text-gofit-ink/60">
              <span className="text-gofit-red font-bold">22</span>
              {t('madeInDz.stat2')}
            </div>
            <div className="flex items-center gap-2 text-gofit-ink/60">
              <span className="text-gofit-red font-bold">2 800</span>
              {t('madeInDz.stat3')}
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 relative aspect-[4/5] rounded-3xl overflow-hidden bg-gofit-fog border border-gofit-ink/10 shadow-[0_30px_80px_rgba(0,0,0,0.08)]"
        >
          <img src="/products/h1-noir.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6 right-6 text-white">
            <div className="text-xs uppercase tracking-[0.3em] text-gofit-red mb-2 font-semibold">{t('madeInDz.cardKicker')}</div>
            <div className="font-display text-3xl font-bold">{t('madeInDz.cardTitle')}</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ------------------------- PRODUCT SHOWCASE ------------------------- */

const ProductCard = ({ product, idx }) => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-10%' });
  const meta = t(`showcase.products.${product.key}`);
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: idx * 0.1, ease: [0.16, 1, 0.3, 1] }}
      className="group relative"
    >
      <Link to={product.href} className="block">
        <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-gofit-fog border border-gofit-ink/5">
          <motion.img
            src={product.image}
            alt={meta?.name || ''}
            className="absolute inset-0 w-full h-full object-cover"
            whileHover={{ scale: 1.06 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            draggable={false}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gofit-ink/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <motion.div
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-gofit-ink text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500"
            whileHover={{ rotate: 45 }}
          >
            <ArrowUpRight className="w-5 h-5" />
          </motion.div>
        </div>
        <div className="mt-5 flex items-start justify-between gap-4">
          <div>
            <div className="font-display font-bold text-xl text-gofit-ink mb-1">{meta?.name}</div>
            <div className="text-sm text-gofit-ink/55">{meta?.sub}</div>
          </div>
          <div className="text-right rtl:text-left">
            <div className="font-display font-bold text-lg text-gofit-ink">{product.price.toLocaleString('fr-FR')}</div>
            <div className="text-xs text-gofit-ink/55">{t('showcase.currency')}</div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

const ProductShowcase = () => {
  const { t, lang } = useLanguage();
  const isAr = lang === 'ar';
  return (
    <section className="bg-gofit-fog text-gofit-ink py-28 md:py-36">
      <div className="container mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <div className="text-gofit-red text-xs uppercase tracking-[0.4em] mb-6 font-medium">
              {t('showcase.kicker')}
            </div>
            <h2 className="font-display font-bold text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              {t('showcase.title1')} <br /> {t('showcase.title2')}
            </h2>
          </div>
          <Link
            to="/collections"
            className="group inline-flex items-center gap-3 text-sm uppercase tracking-[0.2em] font-medium text-gofit-ink/70 hover:text-gofit-ink transition-colors"
          >
            {t('showcase.seeAll')}
            <ArrowRight className={`w-4 h-4 transition-transform group-hover:translate-x-1 ${isAr ? 'rotate-180' : ''}`} />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {featuredProducts.map((p, i) => (
            <ProductCard key={p.id} product={p} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------- SPLIT HOMME / FEMME ------------------------- */

const SplitGenders = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-10%' });
  const blocks = [
    { key: 'men', label: t('split.men'), sub: t('split.menSub'), img: '/products/h1-bleu.jpg', href: '/homme' },
    { key: 'women', label: t('split.women'), sub: t('split.womenSub'), img: '/products/f1-bleu.jpg', href: '/femme' },
  ];
  return (
    <section ref={ref} className="bg-white text-gofit-ink">
      <div className="grid md:grid-cols-2">
        {blocks.map((b, i) => (
          <motion.div
            key={b.key}
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="relative aspect-[4/5] md:aspect-auto md:min-h-[80vh] overflow-hidden group cursor-pointer"
          >
            <Link to={b.href} className="block w-full h-full">
              <motion.img
                src={b.img}
                alt={b.label}
                className="absolute inset-0 w-full h-full object-cover"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gofit-ink/85 via-gofit-ink/30 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 md:bottom-12 md:left-12 md:right-12 text-white">
                <div className="text-gofit-red text-xs uppercase tracking-[0.3em] mb-3 font-semibold">{t('split.kicker')}</div>
                <h3 className="font-display font-bold text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tight mb-3">{b.label}</h3>
                <div className="text-white/70 mb-4 md:mb-6">{b.sub}</div>
                <div className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] font-medium border-b border-white/30 pb-1 group-hover:border-gofit-red group-hover:text-gofit-red transition-all duration-300">
                  {t('split.explore')}
                  <ArrowUpRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

/* --------------------------- FEATURES --------------------------- */
/* Tech section — uses the official annotated flyer (h1-04 / f1-04) as the
   centerpiece. The flyer has FR+AR labels baked in (mesh respirant, talon
   ergonomique, memory foam, antidérapant), so we just need to frame it. */

const Features = () => {
  const { t, lang } = useLanguage();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-10%' });
  const items = t('features.items');
  const list = Array.isArray(items) ? items : [];

  // Use the official annotated flyer (matches what the user requested).
  // h1-04.png is the men's annotated shoe with feature callouts.
  const annotatedSrc = '/marketing/h1-04.png';

  return (
    <section
      ref={ref}
      className="relative bg-gofit-ink text-white py-28 md:py-36 overflow-hidden"
    >
      {/* Subtle red glow */}
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-gofit-red/10 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-6 relative">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="text-gofit-red text-xs uppercase tracking-[0.4em] mb-6 font-medium">
            {t('features.kicker')}
          </div>
          <h2 className="font-display font-bold text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
            {t('features.title1')} <br />
            <span className="text-gofit-red">{t('features.title2')}</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Annotated flyer — centerpiece */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7 relative rounded-3xl overflow-hidden bg-black border border-white/10 shadow-[0_40px_120px_rgba(225,15,30,0.15)]"
            style={{ aspectRatio: '3 / 4.5' }}
          >
            <img
              src={annotatedSrc}
              alt={t('features.title1') + ' ' + t('features.title2')}
              className="absolute inset-0 w-full h-full object-cover object-top"
              draggable={false}
              loading="lazy"
            />
            {/* Soft red corner accents */}
            <div className="absolute top-0 left-0 w-24 h-24 border-t-2 border-l-2 border-gofit-red rounded-tl-3xl pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-24 h-24 border-b-2 border-r-2 border-gofit-red rounded-br-3xl pointer-events-none" />
          </motion.div>

          {/* Feature list — grouped, alternating accents */}
          <div className="lg:col-span-5 space-y-5">
            {list.map((f, i) => {
              const Icon = featureIcons[i] || Footprints;
              return (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, x: lang === 'ar' ? -40 : 40 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.7, delay: 0.1 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  className="group flex gap-4 p-5 md:p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-gofit-red/40 transition-all duration-500 backdrop-blur-sm"
                >
                  <div className="shrink-0 w-12 h-12 rounded-xl bg-gofit-red/15 flex items-center justify-center group-hover:bg-gofit-red transition-colors duration-500">
                    <Icon className="w-6 h-6 text-gofit-red group-hover:text-white transition-colors duration-500" />
                  </div>
                  <div>
                    <div className="flex items-baseline gap-3 mb-1.5">
                      <span className="font-display font-bold text-xs text-gofit-red tracking-wider">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <h3 className="font-display font-bold text-lg md:text-xl">{f.title}</h3>
                    </div>
                    <p className="text-white/65 leading-relaxed text-sm">{f.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ----------------------- LIFESTYLE BANNER ----------------------- */
/* Full-bleed band using the official lifestyle flyer (men or women), with a
   parallax effect and a single bold quote overlaid. */

const LifestyleBanner = ({ image, alt, quote }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['-15%', '15%']);
  return (
    <section ref={ref} className="relative h-[70vh] md:h-[90vh] overflow-hidden bg-gofit-ink">
      <motion.img
        src={image}
        alt={alt}
        style={{ y }}
        className="absolute inset-0 w-full h-[130%] object-cover object-center"
        draggable={false}
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-gofit-ink/85 via-gofit-ink/30 to-gofit-ink/40" />
      <div className="relative z-10 h-full flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-10%' }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-4xl text-center text-white"
        >
          <div className="text-gofit-red text-xs uppercase tracking-[0.4em] mb-6 font-semibold">
            Made in Algeria
          </div>
          <p className="font-display font-bold text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            {quote}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

/* ----------------------- COLOR VARIANTS ----------------------- */
/* Showcase the official "3 couleurs" flyers (h1-02 + f1-02). */

const ColorVariants = () => {
  const { t, lang } = useLanguage();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-10%' });
  const blocks = [
    { src: '/marketing/h1-02.png', label: t('split.men'), alt: 'H1 — 3 couleurs' },
    { src: '/marketing/f1-02.png', label: t('split.women'), alt: 'F1 — 3 couleurs' },
  ];
  return (
    <section ref={ref} className="bg-white text-gofit-ink py-24 md:py-32 border-t border-gofit-ink/5">
      <div className="container mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="text-gofit-red text-xs uppercase tracking-[0.4em] mb-6 font-medium">
              {lang === 'ar' ? '06 / الألوان' : '06 / Coloris'}
            </div>
            <h2 className="font-display font-bold text-5xl md:text-6xl leading-[0.95] tracking-tight">
              {lang === 'ar' ? (
                <>3 ألوان <br /><span className="text-gofit-red">لكل موديل.</span></>
              ) : (
                <>3 coloris <br /><span className="text-gofit-red">par modèle.</span></>
              )}
            </h2>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {blocks.map((b, i) => (
            <motion.div
              key={b.src}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className="relative rounded-3xl overflow-hidden bg-gofit-fog border border-gofit-ink/10 shadow-[0_30px_80px_rgba(0,0,0,0.06)]"
              style={{ aspectRatio: '3 / 5.5' }}
            >
              <img
                src={b.src}
                alt={b.alt}
                className="absolute inset-0 w-full h-full object-cover object-top"
                draggable={false}
                loading="lazy"
              />
              <div className="absolute top-5 left-5 right-5 flex justify-between items-start">
                <span className="px-3 py-1 rounded-full bg-white/95 backdrop-blur-sm text-gofit-ink font-display font-bold text-sm uppercase tracking-wider shadow">
                  {b.label}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* --------------------------- TESTIMONIALS --------------------------- */

const Testimonials = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-10%' });
  const items = t('testimonials.items');
  const list = Array.isArray(items) ? items : [];
  return (
    <section ref={ref} className="bg-gofit-fog text-gofit-ink py-28 md:py-36">
      <div className="container mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="text-gofit-red text-xs uppercase tracking-[0.4em] mb-6 font-medium">
            {t('testimonials.kicker')}
          </div>
          <h2 className="font-display font-bold text-5xl md:text-6xl leading-[0.95] tracking-tight">
            {t('testimonials.title')}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {list.map((tt, i) => (
            <motion.div
              key={tt.name}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="bg-white p-8 rounded-2xl border border-gofit-ink/5 shadow-[0_10px_30px_rgba(0,0,0,0.04)]"
            >
              <div className="flex gap-1 mb-5">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} className="w-4 h-4 fill-gofit-red text-gofit-red" />
                ))}
              </div>
              <p className="text-gofit-ink/80 leading-relaxed mb-6 text-[15px]">&laquo; {tt.text} &raquo;</p>
              <div className="font-semibold text-gofit-ink">{tt.name}</div>
              <div className="text-xs text-gofit-ink/50 uppercase tracking-wider mt-1">{t('testimonials.verified')}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* --------------------------- LOOKBOOK --------------------------- */

const lookbookImages = [
  '/marketing/h1-01.png',
  '/marketing/f1-01.png',
  '/marketing/h1-02.png',
  '/marketing/f1-02.png',
  '/marketing/h1-03.png',
  '/marketing/f1-03.png',
  '/marketing/h1-04.png',
  '/marketing/f1-04.png',
  '/marketing/h1-05.png',
  '/marketing/f1-05.png',
  '/marketing/h1-06.png',
  '/marketing/f1-06.png',
];

const Lookbook = () => {
  const { t, lang } = useLanguage();
  const isAr = lang === 'ar';
  const targetRef = useRef(null);
  const [isDesktop, setIsDesktop] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(min-width: 768px)');
    const apply = () => setIsDesktop(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  const { scrollYProgress } = useScroll({ target: targetRef, offset: ['start start', 'end end'] });
  // Translate horizontally on vertical scroll (desktop only). On mobile we let users swipe.
  const x = useTransform(scrollYProgress, [0, 1], ['0%', '-78%']);

  return (
    <section
      ref={targetRef}
      className={`relative bg-gofit-ink text-white ${isDesktop ? 'h-[320vh]' : ''}`}
    >
      <div className={`${isDesktop ? 'sticky top-0 h-screen' : ''} overflow-hidden flex flex-col justify-center py-20 md:py-0`}>
        <div className="container mx-auto px-6 mb-8 md:mb-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="text-gofit-red text-xs uppercase tracking-[0.4em] mb-4 font-medium">
                {isAr ? '05 / لوكبوك' : '05 / Lookbook'}
              </div>
              <h2 className="font-display font-bold text-5xl md:text-6xl leading-[0.95] tracking-tight">
                {isAr ? (
                  <>المجموعة <br /><span className="text-gofit-red">في صور.</span></>
                ) : (
                  <>La collection <br /><span className="text-gofit-red">en images.</span></>
                )}
              </h2>
            </div>
            <div className="text-white/50 text-sm uppercase tracking-[0.2em] hidden md:block">
              {isAr ? 'مرر للأسفل' : 'Scroll'} ↓
            </div>
          </div>
        </div>

        <motion.div
          style={isDesktop ? { x, direction: 'ltr' } : { direction: 'ltr' }}
          className={`flex items-center gap-4 md:gap-8 px-6 md:px-12 ${isDesktop ? '' : 'overflow-x-auto snap-x snap-mandatory pb-4'}`}
        >
          {lookbookImages.map((src, i) => (
            <motion.div
              key={src}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: (i % 4) * 0.05 }}
              className="shrink-0 relative rounded-2xl overflow-hidden bg-gofit-ink-soft border border-white/5 shadow-[0_30px_80px_rgba(0,0,0,0.4)] snap-center"
              style={{ width: 'min(78vw, 320px)', aspectRatio: '3 / 7' }}
            >
              <img
                src={src}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
                draggable={false}
              />
              <div className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-sm text-[10px] uppercase tracking-wider font-semibold text-white/90">
                {String(i + 1).padStart(2, '0')} / {String(lookbookImages.length).padStart(2, '0')}
              </div>
            </motion.div>
          ))}

          {/* Final card — "view all" CTA */}
          <Link
            to="/collections"
            className="shrink-0 group relative rounded-2xl overflow-hidden border border-gofit-red bg-gofit-red flex flex-col items-center justify-center text-center p-8 hover:bg-gofit-red-600 transition-colors snap-center"
            style={{ width: 'min(78vw, 320px)', aspectRatio: '3 / 7' }}
          >
            <div className="font-display font-bold text-4xl md:text-5xl mb-4">{t('showcase.seeAll')}</div>
            <ArrowRight className={`w-8 h-8 transition-transform group-hover:translate-x-2 ${isAr ? 'rotate-180' : ''}`} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

/* --------------------------- CTA --------------------------- */

const FinalCTA = () => {
  const { t, lang } = useLanguage();
  const isAr = lang === 'ar';
  return (
    <section className="relative bg-gofit-red text-white overflow-hidden">
      <div className="container mx-auto px-6 py-28 md:py-40 text-center relative z-10">
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-10%' }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="font-display font-bold text-6xl sm:text-7xl md:text-8xl lg:text-9xl leading-[0.9] tracking-tight mb-10"
        >
          {t('cta.title1')} <br />
          {t('cta.title2')}
        </motion.h2>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Link
            to="/collections"
            className="group inline-flex items-center gap-3 px-10 py-5 bg-white text-gofit-red font-bold uppercase tracking-wide text-sm rounded-full hover:bg-gofit-ink hover:text-white transition-all duration-300 hover:scale-[1.03]"
          >
            {t('cta.button')}
            <ArrowRight className={`w-5 h-5 transition-transform group-hover:translate-x-1 ${isAr ? 'rotate-180' : ''}`} />
          </Link>
        </motion.div>
      </div>

      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.06] overflow-hidden" style={{ direction: 'ltr' }}>
        <div className="font-display font-black text-[40vw] tracking-tighter whitespace-nowrap">
          GOFIT
        </div>
      </div>
    </section>
  );
};

/* ============================ HOMEPAGE ============================ */

const HomePage = () => {
  const { lang } = useLanguage();

  // Force light mode on home (the rest of the app may have its own theme).
  useEffect(() => {
    const root = document.documentElement;
    const wasDark = root.classList.contains('dark');
    root.classList.remove('dark');
    root.classList.add('light');
    return () => {
      if (wasDark) {
        root.classList.remove('light');
        root.classList.add('dark');
      }
    };
  }, []);

  return (
    <>
      <Helmet>
        <html lang={lang} />
        <title>Gofit — Go Faster. Go Further.</title>
        <meta name="description" content="Gofit — Sneakers de running fabriquées en Algérie. Performance, confort, design." />
        <meta property="og:title" content="Gofit — Go Faster. Go Further." />
        <meta property="og:description" content="Sneakers de running 100% fabriquées en Algérie." />
      </Helmet>

      <div className={`bg-white text-gofit-ink ${lang === 'ar' ? 'font-arabic' : ''}`}>
        <Hero />
        <Ticker />
        <MadeInDz />
        <ProductShowcase />
        <LifestyleBanner
          image="/marketing/h1-03.png"
          alt="Gofit lifestyle"
          quote={lang === 'ar' ? 'الجري كل يوم. التدريب. المشي. الاستعمال اليومي.' : 'Le running quotidien. L\'entraînement. La marche. Le quotidien.'}
        />
        <SplitGenders />
        <Features />
        <ColorVariants />
        <LifestyleBanner
          image="/marketing/f1-04.png"
          alt="Gofit lifestyle femme"
          quote={lang === 'ar' ? 'تصميم نسائي أنيق. للجري، للمشي، للخروج.' : 'Design féminin & élégant. Pour courir, marcher, sortir.'}
        />
        <Lookbook />
        <Testimonials />
        <FinalCTA />
      </div>
    </>
  );
};

export default HomePage;
